import type { CardDef } from '../../../../core/card';
import { cardDescription } from '../../../../core/cardText';
import { PRESS } from '../../../palette';
import { plateCode } from '../codes';
import { pt, rect as rectPts } from '../geom';
import { hazardBands } from '../hatch';
import { box, fillPoly, label, line, solid, type FaceSpec, type Prim } from '../spec';
import { EXCEPTION, PRINT } from '../tune';
import { cjkTitle, latinTitle } from './util';
import { t } from '../../../../i18n';

/**
 * **异常 `/ Exception` —— 白卡。**
 *
 * 这是全牌库唯一一张：白底、用 `PRESS.red`、零酸色、**不上印刷层**、
 * 外发光只在牌外可见。五个「唯一」说的是同一件事：
 *
 * > **它不是这台机器印的。**
 *
 * 所以它连半调、套印错位、颗粒、磨损、注册标记一概没有 ——
 * 有任何一个，这个设定就说不通了。见 `visual-design.md` §2、§6.1。
 *
 * 画面来自 Lissitzky《用红楔子打败白人》（1919）：黑圆是**仪器与准星**，
 * 红楔把它切开。楔尖一个准星，底下一条留白字段「抽到此牌者 ____」。
 */

/** 白卡上的墨：纯黑 + 印刷红。⚠️ 酸色一个都不许进来 */
const INK = 0x000000;
const PAPER = 0xffffff;

export function exceptionFace(card: CardDef): FaceSpec {
  const tune = EXCEPTION;
  const red = PRESS.red;

  /**
   * **图形面**（`spill`）：仪器 + 楔子 + 准星。
   *
   * ⚠️ 它们必须放在 `spill` 而不是 `prims`，因为 `prims` 会被裁到牌内 ——
   *    而打样里红楔是从牌**外**（x = −45）刺进来的，白卡的设计规则也写着
   *    「出血**用满** 0.25×W」。裁在牌边上，"刺穿"这个动作就不成立了。
   *
   * ⚠️ 而且必须**整组**放进来，不能只把楔挪出来：`spill` 画在 `prims` 之前，
   *    单独挪楔会让它落到黑圆的**下面**，而打样里的顺序是"圆 → 楔 → 准星"。
   *    整组按原顺序排，层叠才和打样一致。
   */
  const art: Prim[] = [];

  // ① 仪器：两组同心圆。
  //    ⚠️ 圆心刻意在牌**外**（x = 105 > 100）—— 它是一台对准这张牌的仪器，
  //       不是牌面上的装饰。所以它天然要出血
  //    白圈是"外发光"：它在白纸上不可见，只在牌外亮起来
  art.push(
    { k: 'circle', x: tune.ringCx, y: tune.ringCy, r: tune.ringR1, stroke: { width: 3.4, color: PAPER } },
    { k: 'circle', x: tune.ringCx, y: tune.ringCy, r: tune.ringR2, stroke: { width: 1.6, color: PAPER } },
    { k: 'circle', x: tune.ringCx, y: tune.ringCy, r: tune.ringR1, stroke: { width: 2.6, color: INK } },
    {
      k: 'circle',
      x: tune.ringCx,
      y: tune.ringCy,
      r: tune.ringR2,
      stroke: { width: 0.9, color: INK, alpha: 0.5 },
    },
  );

  // ② 红楔子：先描一圈白边再填红。
  //    白边不是装饰 —— 没有它，红楔和黑圆贴在一起会糊成一坨
  const wedge = [
    pt(tune.wedgeTipX, tune.wedgeTipY),
    pt(tune.wedgeBackX, tune.wedgeTopY),
    pt(tune.wedgeBackX, tune.wedgeBotY),
  ];
  art.push(
    fillPoly(wedge, solid(PAPER), { width: 3.2, color: PAPER }),
    fillPoly(wedge, solid(red)),
  );

  // ③ 楔尖的准星
  art.push(
    line([pt(tune.wedgeTipX, tune.wedgeTipY - tune.markArm), pt(tune.wedgeTipX, tune.wedgeTipY + tune.markArm)], {
      width: 1.2,
      color: INK,
    }),
    line([pt(tune.wedgeTipX - tune.markArm, tune.wedgeTipY), pt(tune.wedgeTipX + tune.markArm, tune.wedgeTipY)], {
      width: 1.2,
      color: INK,
    }),
    { k: 'circle', x: tune.wedgeTipX, y: tune.wedgeTipY, r: 1.4, stroke: { width: 0.8, color: INK } },
  );

  /** **纸面**（`prims`）：印在牌上、不许出框的那些 —— 页眉、表格、正文、危险条纹 */
  const paper: Prim[] = [
    label(plateCode(card.local), 6, 10.4, { size: 4.4, color: INK, weight: 700, tracking: 1.2 }),
    label(t('render.category.lethal'), 94, 10.4, {
      size: 4.1,
      color: red,
      role: 'cjk',
      anchor: 'end',
      tracking: 0.9,
    }),
    line([pt(0, 13.4), pt(100, 13.4)], { width: 0.55, color: INK }),
    label('LOT 0000 · REV 0 · DO NOT DUPLICATE', 6, 18.6, { size: 2.6, color: INK, tracking: 0.5 }),
    // 留白字段。⚠️ 线画到 118（出框）也没关系 —— 它会被裁到 100
    label(t('render.face.exceptionHolder'), 6, 90, {
      size: 3.4,
      color: INK,
      role: 'cjk',
      tracking: 0.4,
    }),
    line([pt(27, 91), pt(118, 91)], { width: 0.5, color: INK }),
    box(0, 94, 100, 22, solid(INK)),
    label(cjkTitle(card), 4.5, 109.5, {
      size: 15,
      color: PAPER,
      role: 'cjk',
      weight: 900,
      tracking: 1.4,
    }),
    label(latinTitle(card), 4.5, 114.4, { size: 3.2, color: PAPER, tracking: 2.1 }),
    label('KILL', 94, 114.4, { size: 3.1, color: red, anchor: 'end', tracking: 1.6 }),
    label(cardDescription(card), 4.5, 122.4, {
      size: 3.4,
      color: INK,
      role: 'cjk',
      wrap: 27,
      lineHeight: 4.7,
    }),
    label('SIG: EXCEPTION — UNCAUGHT', 4.5, 127.6, { size: 2.9, color: red, tracking: 0.6 }),
  ];

  // 底部危险条纹。⚠️ 和**濒死状态的横带共用** `hatch.hazardBands` ——
  //    这不是省事，是让"禁止进入"在整套视觉里只有一个写法
  const strip = rectPts(0, tune.hazardY, 100, tune.hazardH);
  for (const band of hazardBands(strip, PRINT.hazardPitch, PRINT.hazardAngle, PRINT.hazardDuty)) {
    paper.push(fillPoly(band, solid(INK)));
  }

  return {
    ground: solid(PAPER),
    accent: red,
    border: { width: 1, color: INK, alpha: 0.3 },
    prims: paper,
    // ⚠️ 白卡**不上印刷层** —— 这是它和其余 12 张的根本区别
    press: false,
    spill: art,
  };
}
